import { MutableRefObject, ReactNode, useRef, useState } from 'react';
import Button, { ButtonProps } from '../Button/Button';
import Icon, { Icons } from '../Icon';
import {
  FloatingPortal,
  autoUpdate,
  flip,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
} from '@floating-ui/react';

const ChoiceButton = ({
  children,
  options,
  variant,
  size,
  disabled,
  root,
}: {
  children: ReactNode;
  options: {
    label: string;
    onClick: () => void;
  }[];
  variant?: ButtonProps['variant'];
  size?: ButtonProps['size'];
  disabled?: boolean;
  root?: MutableRefObject<any>;
}) => {
  const [showOptions, setShowOptions] = useState(false);

  const { refs, floatingStyles, context } = useFloating({
    open: showOptions,
    onOpenChange: setShowOptions,
    middleware: [offset(8), flip(), shift()],
    placement: 'bottom-end',
    whileElementsMounted: autoUpdate,
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);
  const role = useRole(context);

  // Merge all the interactions into prop getters
  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
    role,
  ]);

  return (
    <div className="relative inline-flex">
      <Button
        ref={refs.setReference}
        {...getReferenceProps()}
        variant={variant}
        size={size}
        disabled={disabled}
        className="pr-3"
      >
        {children}
        <span className="ml-4">
          <Icon name={Icons.CHEVRON_DOWN} size={20} className="text-gray-50" />
        </span>
      </Button>
      {showOptions && (
        <FloatingPortal root={root}>
          <ul
            className="rounded-lg py-2 border border-gray-300 shadow-lg bg-gray-50 whitespace-nowrap min-w-[12ch]"
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
          >
            {options.map((o) => (
              <li key={o.label} className="w-full">
                <button
                  onClick={o.onClick}
                  type="button"
                  className="p-2 text-base font-medium text-gray-800 block w-full bg-gray-50 hover:bg-gray-100 text-left cursor-pointer"
                >
                  {o.label}
                </button>
              </li>
            ))}
          </ul>
        </FloatingPortal>
      )}
    </div>
  );
};

export default ChoiceButton;
